import React, { useState, useEffect } from 'react';
import ReactGA4 from 'react-ga4';

const SPORT_DESCRIPTION_SUFFIXES = [
  'Baseball',
  'Basketball',
  'Football',
  'Hockey',
  'Pokemon',
  'Soccer',
  'F1',
  'Formula 1',
  'Golf',
  'Tennis',
  'NASCAR',
  'WNBA'
];

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const EBAY_SEARCH_URL = 'https://www.ebay.com/sch/i.html';

const DEFAULT_EBAY_SEARCH_PARAMS = {
  _sacat: '0',
  _from: 'R40',
  _trksid: 'p2334524.m570.l1313'
};

const ALLOWED_EBAY_SEARCH_PARAMS = new Set([
  '_sacat',
  '_from',
  '_trksid',
  'LH_Sold',
  'LH_Complete',
  'LH_BIN',
  'LH_Auction',
  '_udlo',
  '_udhi',
  '_sop'
]);

const EPN_AFFILIATE_PARAMS = new Set([
  'campid',
  'customid',
  'mkevt',
  'mkcid',
  'mkrid',
  'siteid',
  'toolid'
]);

export const stripSportFromDescription = (description, sport) => {
  if (!description) return '';

  const suffixes = [
    ...SPORT_DESCRIPTION_SUFFIXES
  ].filter(Boolean);

  const uniqueSuffixes = Array.from(new Set(suffixes)).sort((a, b) => b.length - a.length);
  let displayDescription = String(description).trim();

  uniqueSuffixes.forEach((suffix) => {
    const pattern = new RegExp(`(^|[\\s\\-–—:|/])${escapeRegExp(suffix)}(?=$|[\\s\\-–—:|/])`, 'gi');
    displayDescription = displayDescription.replace(pattern, '$1');
  });

  return displayDescription
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([:/|])/g, '$1')
    .replace(/([:/|])\s*$/g, '')
    .replace(/^\s*([:/|])\s*/g, '')
    .trim();
};

// Build only the plain eBay search destination. Affiliate tracking is added by
// buildTrackedEbaySearchUrl or, for eligible static anchors, by Smart Links.
export const buildEbaySearchUrl = ({ query, params = {} }) => {
  const normalizedQuery = String(query || '').trim();
  if (!normalizedQuery) {
    throw new Error('An eBay search query is required.');
  }

  if (!params || typeof params !== 'object' || Array.isArray(params)) {
    throw new Error('eBay search params must be an object.');
  }

  const searchParams = new URLSearchParams({
    _nkw: normalizedQuery,
    ...DEFAULT_EBAY_SEARCH_PARAMS
  });

  Object.entries(params).forEach(([key, value]) => {
    if (EPN_AFFILIATE_PARAMS.has(key)) {
      throw new Error(`Affiliate parameter "${key}" is managed by eBay Smart Links.`);
    }
    if (!ALLOWED_EBAY_SEARCH_PARAMS.has(key)) {
      throw new Error(`Unsupported eBay search parameter "${key}".`);
    }
    if (value === undefined || value === null || value === false || value === '') {
      searchParams.delete(key);
      return;
    }
    searchParams.set(key, value === true ? '1' : String(value));
  });

  return `${EBAY_SEARCH_URL}?${searchParams.toString()}`;
};

// Build an explicitly tracked destination from the public EPN configuration.
const COIN_SEARCH_ATTRIBUTION = {
  typo: 'typo_hunter',
  builder: 'advanced_search'
};

export const buildTrackedEbaySearchUrl = (search, tool) => {
  const campaign = window._epn?.campaigns?.[tool] || window._epn?.campaign;
  if (!campaign || !/^\d+$/.test(String(campaign))) {
    throw new Error('Search links are temporarily unavailable. Reload the page and try again.');
  }
  const url = new URL(buildEbaySearchUrl(search));
  Object.entries({ campid: String(campaign), mkevt: '1', mkcid: '1',
    mkrid: '711-53200-19255-0', toolid: '10001' }).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });
  // EPN custom IDs provide earnings attribution within a campaign. A distinct
  // campaign can also be assigned per tool through window._epn.campaigns.
  if (COIN_SEARCH_ATTRIBUTION[tool]) {
    url.searchParams.set('customid', COIN_SEARCH_ATTRIBUTION[tool]);
  }
  return url.toString();
};

// Preserve the existing call signature used throughout the dashboard.
export const generateEbaySearchUrl = (description, sport) => {
  return buildTrackedEbaySearchUrl({
    query: stripSportFromDescription(description, sport)
  }, 'grading');
};

// Component to render description as clickable link on desktop, plain text on mobile
export const DescriptionDisplay = ({ description, sport, ebaySearchUrl, className = '' }) => {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 767);
  const displayDescription = stripSportFromDescription(description, sport);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 767);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (isDesktop) {
    return (
      <a 
        href={ebaySearchUrl || generateEbaySearchUrl(displayDescription)}
        target="_blank"
        rel="noopener noreferrer"
        className={`${className} ebay-link`}
        style={{ 
          color: 'inherit', 
          textDecoration: 'underline',
          cursor: 'pointer'
        }}
        onClick={(e) => {
          // Track the click event
          ReactGA4.event('ebay_search_click', {
            description: displayDescription
          });
        }}
      >
        {displayDescription}
      </a>
    );
  }

  // Mobile view - just show plain text
  return <span className={className}>{displayDescription}</span>;
};
